import React, {useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'
import DatePicker from 'react-datepicker'
import {
  Button,
  Checkbox,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core'
import useAuth from 'features/auth/useAuth'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import CardWithTitle from 'components/atoms/CardWithTitle'
import NoticeConfirmDialog from 'components/Dialog/NoticeConfirmDialog'
import MemberRegisterStyle from 'pages/CustomerManagement/MemberEdit/Style'
import OrganizationNameInput from 'pages/CustomerManagement/MemberEdit/Form/OrganizationNameInput'
import UserNameInput from 'pages/CustomerManagement/MemberEdit/Form/UserNameInput'
import EmailInput from 'pages/CustomerManagement/MemberEdit/Form/EmailInput'
import CountrySelect from 'pages/CustomerManagement/MemberEdit/Form/CountrySelect'
import AddressInput from 'pages/CustomerManagement/MemberEdit/Form/AddressInput'
import TelephoneNumberInput from 'pages/CustomerManagement/MemberEdit/Form/TelephoneNumberInput'
import SelectedIndustry from 'pages/CustomerManagement/MemberEdit/Form/SelectedIndustry'
import NursingInstitutionInput from 'pages/CustomerManagement/MemberEdit/Form/InstitutionInput'
import BusinessNoInput from 'pages/CustomerManagement/MemberEdit/Form/BusinessNoInput'
import TableHospitalInformation, {
  SelectedListType,
} from 'pages/CustomerManagement/MemberRegistration/Form/TableHospitalInfo'
import useOrgRegister from 'features/invoice/useOrgRegister'
import {
  OrgDetailEditRequest,
  OrgDetailResponse,
  updateOrgDetailApi,
} from 'api/invoiceApis'
import {fetchCountryList} from 'api/commonApi'
import {Agency, getListAgencyApi, getListDeviceApi} from 'api/orgApi'
import RouteConstant from 'constants/RouteConstant'
import {useBoldInputStyle} from 'components/common/useBoldInputStyle'
import {Add, CloseRounded} from '@material-ui/icons'
import AgencyInput from '../MemberRegistration/Form/AgencyInput'

const useStyles = makeStyles(MemberRegisterStyle)

function MemberEditPage() {
  const {t} = useTranslation()
  const classes = useStyles()
  const {loading} = useAuth()
  const history = useHistory()
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()
  const {selected, onFetch} = useOrgRegister()
  const inputClasses = useBoldInputStyle()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const orgId = queryParams.get('orgId')
  const [orgType, setOrgType] = useState(false)
  const [therapyYn, setTherapyYn] = useState(false)
  const [minDate, setMinDate] = useState<Date>()
  const [packageStartDate, setPackageStartDate] = useState<Date | null>(null)
  const [packageExpireDate, setPackageExpireDate] = useState<Date | null>(null)
  const [selectedDetails, setSelectedDetails] = useState<SelectedListType[]>([])
  const [twoFactorType, setTwoFactorType] = useState(false)
  const [listManufacturer, setListManufacturer] = useState<string[]>([])
  const [listDevice, setListDevice] = useState<
    Record<
      string,
      {
        deviceId: number
        deviceName: string
      }[]
    >
  >({})
  const [listDeviceSelected, setListDeviceSelected] = useState<
    {
      manufacturer: string
      deviceId: number
    }[]
  >([
    {
      manufacturer: '',
      deviceId: 0,
    },
  ])
  const [isUsingEdfDevice, setIsUsingEdfDevice] = useState(false)
  const [isPerformAsAgency, setIsPerformAsAgency] = useState(false)
  const [listAgencies, setListAgencies] = useState<Agency[]>([])

  const getValueView = (dataInput: OrgDetailResponse) => {
    return {
      agencyId: dataInput.agencyId ?? -1,
      orgId: dataInput.orgId,
      address: dataInput.address,
      businessNo: dataInput.businessNumber,
      countryId: dataInput.countryId,
      detailIndustry: {
        id: dataInput.detailIndustryId,
        parent: 1,
        depth: 1,
        title: '',
      },
      industryId: dataInput.industryId,
      externalEdfYn: dataInput.externalEdfYn,
      deviceIds: dataInput.deviceIds,
      detailIndustryId: dataInput.detailIndustryId,
      email: dataInput.email,
      firstUserName: dataInput.firstName,
      industry: {
        id: dataInput.industryId,
        parent: 1,
        depth: 1,
        title: '',
      },
      institutionNo: dataInput.institutionNumber,
      lastUserName: dataInput.lastName,
      orgName: dataInput.orgName,
      orgType: dataInput.orgType ? dataInput.orgType : 'NORMAL',
      tel: dataInput.phoneNumber,
      packageExpireDate: dataInput.packageExpireDate,
      packageStartDate: dataInput.packageStartDate,
      therapyYn: dataInput.therapyYn,
      twoFactorType: dataInput.twoFactorType === 'Email',
    } as OrgDetailEditRequest
  }

  const createMemberInfo = useForm({defaultValues: getValueView(selected)})
  const {setError} = createMemberInfo

  const onUpdateOrgInfo = async (orgInfo: OrgDetailEditRequest) => {
    try {
      const response = await updateOrgDetailApi(orgInfo)
      if (response) {
        onSuccessModalOpen(t('IEditORGSuccessMessage')).then(() => {
          history.push(
            `${RouteConstant.CUSTOMER_INQUIRY.path}?orgId=${orgInfo.orgId}`,
          )
        })
      }
    } catch (e) {
      onFailureModalOpen(e?.message ?? t('IRegisterFail'))
    }
  }

  const handleCreate = (orgData: OrgDetailEditRequest) => {
    if (!orgType) orgData.healthProductIds = null
    onUpdateOrgInfo(orgData)
  }
  const convertStringFromDateTime = (data: Date | null) => {
    if (data) {
      const date = new Date(data)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    return ''
  }
  const submit = async (data: OrgDetailEditRequest) => {
    const today = new Date()

    if (!packageStartDate && packageExpireDate) {
      data.packageStartDate = convertStringFromDateTime(today)
    } else {
      data.packageStartDate = packageStartDate
        ? convertStringFromDateTime(packageStartDate)
        : null
    }

    if (
      isUsingEdfDevice &&
      listDeviceSelected.some((item) => item.deviceId === 0)
    ) {
      onFailureModalOpen(t('ISelectDevice'))
      return
    }

    if (isPerformAsAgency && data.agencyId === -1) {
      setError('agencyId', {
        type: 'custom',
        message: `${t('IRequired')}`,
      })
      return
    }

    data.packageExpireDate = packageExpireDate
      ? convertStringFromDateTime(packageExpireDate)
      : null
    data.therapyYn = therapyYn ? 'Y' : 'N'

    const responseCountry = await fetchCountryList()
    const countries = responseCountry.list
    const countryCode =
      countries.find((item) => item.id === data.countryId)?.code || ''
    const arrProductIds = selectedDetails.map((item) => item.productId)
    handleCreate({
      ...data,
      countryCode,
      healthProductIds: arrProductIds.length > 0 ? arrProductIds : null,
      externalEdfYn: isUsingEdfDevice ? 'Y' : 'N',
      ...(isUsingEdfDevice && {
        deviceIds: Array.from(
          new Set(listDeviceSelected.map((item) => item.deviceId)),
        ),
      }),
      agencyId: isPerformAsAgency ? data.agencyId : null,
    })
  }

  const getListCalibrationDevice = async () => {
    const res = await getListDeviceApi()
    const listDuplicatedManu = res.list.map((item) => item.manufacturer)
    setListManufacturer(Array.from(new Set(listDuplicatedManu)))
    const calibrationDeviceList = res.list.reduce<
      Record<
        string,
        {
          deviceId: number
          deviceName: string
        }[]
      >
    >((acc, cur) => {
      if (!acc[cur.manufacturer]) {
        acc[cur.manufacturer] = []
      }
      acc[cur.manufacturer].push({
        deviceId: cur.deviceId,
        deviceName: cur.deviceName,
      })
      return acc
    }, {})
    setListDevice(calibrationDeviceList)
  }

  const onSelectDevice = (
    value: string,
    index: number,
    type: 'manufacturer' | 'deviceId',
  ) => {
    const newList = [...listDeviceSelected]
    if (type === 'manufacturer') {
      newList[index].manufacturer = value
      newList[index].deviceId = 0
    } else {
      newList[index].deviceId = Number(value)
    }
    setListDeviceSelected(newList)
  }

  const onDeleteDevice = (index: number) => {
    const newList = [...listDeviceSelected]
    newList.splice(index, 1)
    setListDeviceSelected(newList)
  }

  const handleCheckboxProductType = (details: SelectedListType[]) => {
    setSelectedDetails([...details])
  }
  const handleValueStartDate = (data: Date | null) => {
    if (data) {
      const newDate = new Date(data)
      newDate.setDate(newDate.getDate() + 1)
      setMinDate(newDate)
      if (packageExpireDate && data >= packageExpireDate) {
        setPackageExpireDate(null)
      }
    } else {
      const newDate = new Date()
      if (packageExpireDate && new Date() >= packageExpireDate) {
        setPackageExpireDate(null)
      }
      newDate.setDate(newDate.getDate() + 1)
      setMinDate(newDate)
    }
    setPackageStartDate(data)
  }
  const handleValueExpireDate = (data: Date | null) => {
    setPackageExpireDate(data)
  }

  const handleCheckboxChangeOrgType = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked
    setOrgType(isChecked)
    if (isChecked) {
      setTwoFactorType(true)
      createMemberInfo.setValue('twoFactorType', isChecked)
    }
  }

  const handle2FACheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    setTwoFactorType(isChecked)
    createMemberInfo.setValue('twoFactorType', isChecked)
  }

  const handleCheckboxChangeTherapyYn = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked
    setTherapyYn(isChecked)
  }

  const getListAgency = async () => {
    const res = await getListAgencyApi()
    setListAgencies(res.list)
  }

  useEffect(() => {
    getListCalibrationDevice()
    getListAgency()
    if (orgId && Number(orgId)) {
      onFetch({id: Number(orgId)})
    }
  }, [])

  useEffect(() => {
    if (selected) {
      createMemberInfo.reset(getValueView(selected))
      if (selected.orgType === 'HEALTH') setOrgType(true)
      else setOrgType(false)

      if (selected.therapyYn === 'Y') setTherapyYn(true)
      else setTherapyYn(false)

      if (selected.twoFactorType === 'Email') setTwoFactorType(true)
      else setTwoFactorType(false)

      const isExternalEdfEnabled = selected.externalEdfYn === 'Y'
      setIsUsingEdfDevice(isExternalEdfEnabled)

      if (isExternalEdfEnabled) {
        if (selected.deviceIds && selected.deviceIds.length > 0) {
          const devicesSelected = selected.deviceIds.map((deviceId) => {
            const matchedEntry = Object.entries(listDevice).find(
              ([, devices]) => devices.some((d) => d.deviceId === deviceId),
            )

            if (matchedEntry) {
              const [manufacturer, devices] = matchedEntry
              const device = devices.find((d) => d.deviceId === deviceId)

              if (device) {
                return {
                  manufacturer,
                  deviceId: device.deviceId,
                }
              }
            }

            return {
              manufacturer: '',
              deviceId: 0,
            }
          })
          setListDeviceSelected(devicesSelected)
        } else {
          setListDeviceSelected([
            {
              manufacturer: '',
              deviceId: 0,
            },
          ])
        }
      } else {
        setListDeviceSelected([
          {
            manufacturer: '',
            deviceId: 0,
          },
        ])
      }

      setIsPerformAsAgency(Boolean(selected.agencyId && selected.agencyId > 0))

      if (selected.packageStartDate) {
        setPackageStartDate(new Date(selected.packageStartDate))
        const newDate = new Date(selected.packageStartDate)
        newDate.setDate(newDate.getDate() + 1)
        setMinDate(newDate)
      } else {
        const newDate = new Date()
        newDate.setDate(newDate.getDate() + 1)
        setMinDate(newDate)
      }

      if (selected.packageExpireDate) {
        setPackageExpireDate(new Date(selected.packageExpireDate))
      }

      setSelectedDetails(selected.healthProductInfoList)
    }
  }, [selected])

  useEffect(() => {
    createMemberInfo.setValue('orgType', orgType ? 'HEALTH' : 'NORMAL')
    if (!orgType) {
      setSelectedDetails([])
    }
  }, [orgType])

  return (
    <>
      {loading && <div>loading</div>}
      <div className={classes.topRoot}>
        <FormProvider {...createMemberInfo}>
          <form
            onSubmit={createMemberInfo.handleSubmit((data) =>
              submit(data as OrgDetailEditRequest),
            )}
          >
            <CardWithTitle
              title={t('IUserInfo')}
              className={classes.formRegister}
            >
              <div className={classes.inputContainer}>
                <div style={{height: 24}}>
                  <Checkbox
                    color='primary'
                    checked={orgType}
                    onChange={handleCheckboxChangeOrgType}
                    disabled
                    style={{height: 24, paddingLeft: 0}}
                  />
                  {t('INoteCenter')}
                </div>
                <OrganizationNameInput />
                <UserNameInput />
                <EmailInput />
                <CountrySelect countryId={getValueView(selected).countryId} />
                <AddressInput />
                <TelephoneNumberInput />
                <SelectedIndustry />
                <BusinessNoInput />
                <NursingInstitutionInput />
                {isPerformAsAgency && (
                  <AgencyInput
                    listAgency={listAgencies}
                    agencyId={selected.agencyId ? selected.agencyId : -1}
                  />
                )}

                {/* <div>
                  <p
                    className={inputClasses.inputLabel}
                    style={{padding: '5px 0'}}
                  >
                    {t('IAccountUsagePeriod')}
                  </p>
                  <div className={inputClasses.inputContainer}>
                    <div className={inputClasses.inputWrap}>
                      <DatePicker
                        selected={packageStartDate}
                        onChange={(date) => handleValueStartDate(date)}
                        placeholderText='yyyy-MM-dd'
                        className={inputClasses.input}
                      />
                    </div>
                    <div className={inputClasses.inputWrap}>
                      <DatePicker
                        selected={packageExpireDate}
                        onChange={(date) => handleValueExpireDate(date)}
                        placeholderText='yyyy-MM-dd'
                        minDate={minDate}
                        className={inputClasses.input}
                      />
                    </div>
                  </div>
                </div> */}
                <div
                  style={{
                    display: 'grid',
                    alignItems: 'center',
                    width: '100%',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    marginTop: '5px',
                  }}
                >
                  <div style={{height: 24}}>
                    <Checkbox
                      color='primary'
                      checked={therapyYn}
                      onChange={handleCheckboxChangeTherapyYn}
                      style={{height: 24, paddingLeft: 0}}
                    />
                    {t('IEnablePBMFeature')}
                  </div>
                  <div style={{height: 24}}>
                    <Checkbox
                      color='primary'
                      style={{height: 24, paddingLeft: 0}}
                      checked={twoFactorType}
                      onChange={handle2FACheckbox}
                      disabled={!orgType}
                    />
                    {t('IEmailVerification')}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '5px',
                    }}
                  >
                    <Checkbox
                      color='primary'
                      style={{height: 24, paddingLeft: 0}}
                      checked={isUsingEdfDevice}
                      onChange={(e) => {
                        const isChecked = e.target.checked
                        setIsUsingEdfDevice(isChecked)
                        if (!isChecked) {
                          setListDeviceSelected([
                            {
                              manufacturer: '',
                              deviceId: 0,
                            },
                          ])
                        }
                      }}
                    />
                    {t('IUsingDeviceEDF')}
                    {isUsingEdfDevice && (
                      <Button
                        variant='contained'
                        color='primary'
                        disableElevation
                        type='button'
                        style={{
                          marginLeft: 20,
                        }}
                        disabled={listDeviceSelected.length >= 10}
                        onClick={() =>
                          setListDeviceSelected((prev) => [
                            ...prev,
                            ...[
                              {
                                manufacturer: '',
                                deviceId: 0,
                              },
                            ],
                          ])
                        }
                      >
                        <Add style={{fontSize: 16, marginRight: 4}} />
                        {t('IAdd')}
                      </Button>
                    )}
                  </div>
                  <div style={{height: 24, marginTop: 5}}>
                    <Checkbox
                      color='primary'
                      style={{height: 24, paddingLeft: 0}}
                      checked={isPerformAsAgency}
                      onChange={(e) => setIsPerformAsAgency(e.target.checked)}
                    />
                    {t('IPerformAsAgency')}
                  </div>
                </div>
                {isUsingEdfDevice && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    {listDeviceSelected.map((item, index) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 20,
                        }}
                      >
                        {t('IDevice')}:
                        <Select
                          value={item.manufacturer}
                          label={t('IManufacturingCompany')}
                          id='manufacturer-select'
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || selected === '') {
                              return (
                                <span style={{color: '#9e9e9e'}}>
                                  {t('IManufacturingCompany')}
                                </span>
                              )
                            }
                            return String(selected)
                          }}
                          onChange={(e) => {
                            onSelectDevice(
                              String(e.target.value),
                              index,
                              'manufacturer',
                            )
                          }}
                          style={{
                            width: 200,
                            height: 40,
                          }}
                          input={
                            <OutlinedInput
                              name='manufacturer'
                              id='manufacturer-select'
                            />
                          }
                        >
                          {listManufacturer.map((item) => (
                            <MenuItem value={item} key={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                        <Select
                          value={item.deviceId}
                          label={t('IModelName')}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || Number(selected) === 0) {
                              return (
                                <span style={{color: '#9e9e9e'}}>
                                  {t('IModelName')}
                                </span>
                              )
                            }

                            const selectedDevice = (
                              listDevice[item.manufacturer] ?? []
                            ).find(
                              (device) => device.deviceId === Number(selected),
                            )
                            return (
                              selectedDevice?.deviceName ?? String(selected)
                            )
                          }}
                          onChange={(e) => {
                            onSelectDevice(
                              String(e.target.value),
                              index,
                              'deviceId',
                            )
                          }}
                          style={{
                            width: 200,
                            height: 40,
                          }}
                          input={<OutlinedInput name='model' id='model_name' />}
                        >
                          {(listDevice[item.manufacturer] ?? []).map((item) => (
                            <MenuItem value={item.deviceId} key={item.deviceId}>
                              {item.deviceName}
                            </MenuItem>
                          ))}
                        </Select>
                        {listDeviceSelected.length > 1 && (
                          <CloseRounded
                            style={{
                              cursor: 'pointer',
                              color: 'red',
                            }}
                            onClick={() => onDeleteDevice(index)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* {orgType && (
                  <div className={classes.tableFlexBox}>
                    <div className={classes.tableEdit}>
                      <TableHospitalInformation
                        products={selected.healthProductInfoList}
                        onCheckboxChange={handleCheckboxProductType}
                      />
                    </div>
                  </div>
                )} */}
                <Typography className={classes.padding} />
                <div className={classes.submitButtonContainer}>
                  <Button
                    variant='contained'
                    color='primary'
                    disableElevation
                    type='submit'
                  >
                    {t('Save')}
                  </Button>
                </div>
              </div>
            </CardWithTitle>
          </form>
        </FormProvider>
      </div>
      <NoticeConfirmDialog />
    </>
  )
}

export default MemberEditPage
